import type {
    NextStepsFragment,
    NextStepsFragment_nutshellNotification_notifiables as Notifiable,
} from 'nutshell-graphql-types';

import type {NextStepType} from './types';

export function getInitialValuesFromNextSteps(nextSteps: any, type?: NextStepType | null) {
    if (!type) {
        return null;
    } else if (type === 'slackMessage') {
        return nextSteps.slackMessage;
    } else if (type === 'nutshellNotification') {
        return nextSteps.nutshellNotification
            ? {
                  ...nextSteps.nutshellNotification,
                  notifiables: nextSteps.nutshellNotification.notifiables.map(
                      // @ts-expect-error
                      (notifiable) => notifiable.id
                  ),
              }
            : null;
    } else {
        return null;
    }
}

export const getApiNextStepType = (type: NextStepType | 'nutshellNotificationSelf') => {
    if (type === 'nutshellNotificationSelf') {
        return 'nutshellNotification';
    } else {
        return type;
    }
};

export const getNextStepsCardTopRow = (onlyAllowNotifyingSelf?: boolean) => {
    return onlyAllowNotifyingSelf ? 'Send me a notification' : 'Send a notification';
};

export const getNextStepsCardBottomRow = (
    notifiables: Notifiable[],
    onlyAllowNotifyingSelf?: boolean
) => {
    if (onlyAllowNotifyingSelf) {
        return 'Notify me via Nutshell';
    } else {
        return notifiables
            .map((notifiable) => {
                return notifiable.__typename === 'User' ? notifiable.name : null;
            })
            .filter((notifiable) => notifiable)
            .join(', ');
    }
};

/**
 * If a slack channel is no longer accessible, it will not be in the redux form
 * data, but it will be returned from GraphQL. We want to make sure to pass it
 * to the mutation so we can update the channel id of the existing/broken slack
 * message instead of creating a new notification.
 *
 * @param {string} formId - The id of the form from redux form data
 * @param {string} existingNextStepId - The id of the existing next step from GraphQL
 * @returns {string} The id of the slack action to pass to the submit mutation
 */

export const getSlackActionIdFromFormOrExistingNextStep = (
    formId?: string | null,
    existingNextStepId?: string | null
) => {
    return formId || existingNextStepId;
};

/**
 * Checks if a next step is unconfigured or broken (e.g., slack message with null channel)
 *
 * @param {string} nextStepType - The type of next step (e.g., 'slackMessage', 'nutshellNotification')
 * @param {NextStepsFragment_slackMessage | NextStepsFragment_nutshellNotification} nextStep - The next step data from GraphQL
 * @returns {boolean} - True if the next step needs configuration
 */
export const isNextStepUnconfigured = (
    nextStepType: string,
    nextSteps: NextStepsFragment
): boolean => {
    // @ts-expect-error
    const nextStep = nextSteps[nextStepType];

    if (!nextStep) {
        return true;
    }

    // Slack message with null channel is considered unconfigured
    if (nextStepType === 'slackMessage' && !nextStep.slackChannel) {
        return true;
    }

    return false;
};
